/**
 * Wizard Steg 3: Komplettera
 *
 * Dynamiska sektioner baserat på triggers från SIE-data
 * Enligt K2-regler och CLAUDE.md
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  ArrowRight,
  ArrowLeft,
  Users,
  FileText,
  Shield,
  AlertTriangle,
  Building2,
  Clock,
  Landmark,
  Link2,
  Wrench,
  ClipboardList,
  Factory,
  Sparkles,
  XCircle,
  Plus,
  BookOpen,
} from "lucide-react";

// Indicator component for triggered notes (SIE or manual entry)
function TriggeredNoteBadge({ isManual }: { isManual?: boolean }) {
  if (isManual) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
        <Sparkles size={12} />
        Baserat på angivna värden
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
      <Sparkles size={12} />
      Identifierad i SIE
    </span>
  );
}

// Toggle switch component
function ToggleSwitch({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        checked ? 'bg-primary-600' : 'bg-gray-200'
      }`}
    >
      {label && <span className="sr-only">{label}</span>}
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}
import { toast } from "sonner";
import { formatNumber } from "@/lib/format";
import { reportsApi, companiesApi, type AnnualReport, type Company, type BoardMember, type UserInput, type Calculations, type FixedAssetNoteDetail } from "@/lib/api";
import { BoardMembersSection } from "@/components/BoardMembersSection";
import { FixedAssetNoteCard, createEmptyFixedAssetNote, extractFixedAssetFromSie } from "./FixedAssetNoteCard";
import { isManualEntry, applyOverridesToSieData } from "@/lib/manual-entry";
import {
  K3DeferredTaxSection,
  K3RevenueSplitSection,
  K3ComponentDepreciationSection,
  K3LeaseSection,
  K3ComparativeNotesSection,
} from "@/components/k3";
import { ConsolidationWizard } from "./ConsolidationWizard";

interface Step3CompleteProps {
  report: AnnualReport;
  company: Company;
  onUpdate: (report: AnnualReport) => void;
  /** Override save (for guest/public wizard) */
  onSaveInput?: (data: Record<string, unknown>) => Promise<{ success: boolean; report?: AnnualReport; error?: string }>;
  /** Override navigation (for guest/public wizard or K3 decimal steps) */
  onNavigateStep?: (step: number | string) => void;
  /** Guest mode: different save behavior */
  guestMode?: boolean;
  /** Board members for guest mode (managed by parent) */
  guestBoardMembers?: BoardMember[];
  /** Callback to save board members locally in guest mode */
  onGuestBoardMembersChange?: (members: BoardMember[]) => void;
  /** Callback to persist board member data to localStorage */
  onSaveLocalBoardMembers?: (members: Array<{ name: string; role: string; personalId: string }>) => void;
  /** Previous step (default: 2, guest mode: 3, K3: "2.5") */
  prevStep?: number | string;
  /** Next step (default: 4, guest mode: 5) */
  nextStep?: number | string;
}

interface FormData {
  businessDescription: string;
  significantEvents: string;
  eventsAfterBalanceDate: string;
  averageEmployees: number;
  // Ställda säkerheter - förbättrad med typ
  hasPledgedAssets: string;
  pledgedAssetsType: 'corporate_mortgage' | 'property_mortgage' | 'chattel_mortgage' | 'other';
  pledgedAssetsDescription: string;
  pledgedAssetsAmount: number;
  // Eventualförpliktelser
  hasContingentLiabilities: string;
  contingentLiabilitiesDescription: string;
  contingentLiabilitiesAmount: number;
  // Skulder >5 år
  hasLiabilitiesOver5Years: string;
  liabilitiesOver5YearsAmount: number;
  liabilitiesOver5YearsComment: string;
  // Goodwill avskrivningstid
  goodwillDepreciationYears: number;
  goodwillDepreciationReason: string;
  // Lån till ledande befattningshavare
  hasLoansToBoard: string;
  loansToBoardAmount: number;
  loansToCEOAmount: number;
  // Närstående transaktioner
  hasRelatedPartyTransactions: string;
  relatedPartyType: 'owner' | 'board' | 'ceo' | 'family' | 'related_company' | 'other';
  relatedPartyDescription: string;
  relatedPartyAmount: number;
  // Moderföretag
  isSubsidiary: string;
  parentCompanyName: string;
  parentCompanyOrgNumber: string;
  parentCompanyMunicipality: string;
  // Avskrivningar för anläggningstillgångar
  intangibleDepreciationMethod: string;
  intangibleUsefulLife: number;
  buildingsDepreciationMethod: string;
  buildingsUsefulLife: number;
  machineryDepreciationMethod: string;
  machineryUsefulLife: number;
  // Tjänste-/entreprenaduppdrag
  hasServiceContracts: string;
  serviceContractMethod: 'huvudregeln' | 'alternativregeln';
  serviceContractIndirectCosts: boolean;
  // Egentillverkade varor
  hasSelfManufacturedGoods: string;
  selfManufacturedGoodsIndirectCosts: boolean;
  // Exceptionella poster (ÅRL 5:19)
  hasExceptionalItems: string;
  exceptionalItemDescription: string;
  exceptionalItemAmount: number;
  exceptionalItemIsIncome: boolean;
}

export function Step3Complete({ report, company, onUpdate, onSaveInput, onNavigateStep, guestMode, guestBoardMembers, onGuestBoardMembersChange, onSaveLocalBoardMembers, prevStep = 2, nextStep = 4 }: Step3CompleteProps) {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [boardMembersLoaded, setBoardMembersLoaded] = useState(false);

  const calculations = report.calculations as Calculations | null;
  const triggeredNotes = calculations?.triggeredNotes || [];
  const userInput = report.userInput as UserInput;
  const prevInput = report.previousYearUserInput as UserInput | null | undefined;
  const [showSignificantEvents, setShowSignificantEvents] = useState(!!userInput.significantEvents);
  const [showEventsAfterBalanceDate, setShowEventsAfterBalanceDate] = useState(!!userInput.eventsAfterBalanceDate);

  // Pre-populate fixed asset notes from SIE raw data if available
  const rawSie = report.parsedSieData;
  const sieOpeningBalances = rawSie?.openingBalances || [];
  const sieClosingBalances = rawSie?.closingBalances || [];

  // Check if this is manual entry (no SIE file)
  const isManual = isManualEntry(rawSie);

  // Check if this is K3 accounting standard
  const isK3 = report.accountingStandard === "K3";
  const totalRevenue = calculations?.keyFigures?.netSales || 0;

  // K3: Buildings value for component depreciation (konto 11xx)
  const buildingsValue = sieClosingBalances
    .filter((b) => b.accountNumber.startsWith("11"))
    .reduce((sum, b) => sum + Math.abs(b.amount || 0), 0);

  // K3: Revenue split state
  const [revenueSplit, setRevenueSplit] = useState(userInput.revenueSplit);

  // K3: Component depreciation state
  const [componentDepreciation, setComponentDepreciation] = useState(userInput.componentDepreciation);

  // K3: Leasing state
  const [leasing, setLeasing] = useState(userInput.leasing);

  // K3: Comparative notes state (jämförelsetal)
  const [comparativeNotes, setComparativeNotes] = useState(userInput.comparativeNotes);

  // K3: Koncernredovisning (expandable)
  const [showConsolidationWizard, setShowConsolidationWizard] = useState(false);

  // Check if company has subsidiaries configured
  const hasSubsidiaries = isK3 && userInput.subsidiaries && userInput.subsidiaries.length > 0;

  // Compute effective SIE data with overrides applied (for manual entry values from Step 2)
  const effectiveSie = rawSie && userInput.overrides
    ? applyOverridesToSieData(rawSie, userInput.overrides as Record<string, unknown>)
    : rawSie;

  const getInitialNote = (
    type: "intangible" | "buildings" | "machinery",
    rangeStart: number,
    rangeEnd: number,
    defaultUsefulLife: number,
  ): FixedAssetNoteDetail => {
    // Priority: saved user data > SIE extraction > empty defaults
    if (userInput.fixedAssetNotes?.[type]) return userInput.fixedAssetNotes[type]!;

    const method = userInput.fixedAssetDepreciation?.[type]?.method || "Linjär";
    const life = userInput.fixedAssetDepreciation?.[type]?.usefulLife || defaultUsefulLife;

    // Try to extract from raw SIE data
    if (sieOpeningBalances.length > 0 || sieClosingBalances.length > 0) {
      const extracted = extractFixedAssetFromSie(
        sieOpeningBalances, sieClosingBalances,
        rangeStart, rangeEnd, method, life,
      );
      if (extracted) return extracted;
    }

    return { ...createEmptyFixedAssetNote(), depreciationMethod: method, usefulLife: life };
  };

  // Detailed fixed asset notes state (managed outside react-hook-form)
  const [intangibleNote, setIntangibleNote] = useState<FixedAssetNoteDetail>(
    () => getInitialNote("intangible", 1000, 1099, 5)
  );
  const [buildingsNote, setBuildingsNote] = useState<FixedAssetNoteDetail>(
    () => getInitialNote("buildings", 1100, 1199, 25)
  );
  const [machineryNote, setMachineryNote] = useState<FixedAssetNoteDetail>(
    () => getInitialNote("machinery", 1200, 1299, 5)
  );
  const [intangibleEnabled, setIntangibleEnabled] = useState(true);
  const [buildingsEnabled, setBuildingsEnabled] = useState(true);
  const [machineryEnabled, setMachineryEnabled] = useState(true);

  // Additional (dynamic) notes
  const [additionalNotes, setAdditionalNotes] = useState<Array<{ noteId: string; title: string; content: string }>>(
    userInput.additionalNotes || []
  );
  const [selectedAdditionalNote, setSelectedAdditionalNote] = useState("");

  // Available notes for the dropdown
  const AVAILABLE_ADDITIONAL_NOTES = [
    { id: "RENTAL_RIGHTS", title: "Hyresrätter och liknande rättigheter" },
    { id: "IMPROVEMENT_COSTS", title: "Förbättringsutgifter på annans fastighet" },
    { id: "ACCRUED_EXPENSES", title: "Upplupna kostnader och förutbetalda intäkter" },
    { id: "PREPAID_EXPENSES", title: "Förutbetalda kostnader och upplupna intäkter" },
    { id: "LEASING", title: "Leasingavtal" },
    { id: "PROVISIONS", title: "Avsättningar" },
    { id: "OVERDRAFT_FACILITY", title: "Checkräkningskredit" },
    { id: "INVENTORY_NOTE", title: "Varulager" },
    { id: "EQUITY_CHANGES", title: "Förändring av eget kapital" },
    { id: "TAX_NOTE", title: "Skatt på årets resultat" },
    { id: "UNTAXED_RESERVES_NOTE", title: "Obeskattade reserver" },
    { id: "OTHER_NOTE", title: "Övrig not" },
  ];

  const availableNotesFiltered = AVAILABLE_ADDITIONAL_NOTES.filter(
    (n) => !additionalNotes.some((an) => an.noteId === n.id)
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      businessDescription: userInput.businessDescription || "",
      significantEvents: userInput.significantEvents || "",
      eventsAfterBalanceDate: userInput.eventsAfterBalanceDate || "",
      averageEmployees: userInput.averageEmployees ?? undefined,
      hasPledgedAssets: userInput.pledgedAssets !== undefined
        ? ((userInput.pledgedAssets?.length || 0) > 0 ? "yes" : "no")
        : "yes",
      pledgedAssetsType: userInput.pledgedAssets?.[0]?.type || "corporate_mortgage",
      pledgedAssetsDescription: userInput.pledgedAssets?.[0]?.description || "",
      pledgedAssetsAmount: userInput.pledgedAssets?.[0]?.amount ?? undefined,
      hasContingentLiabilities: userInput.contingentLiabilities !== undefined
        ? ((userInput.contingentLiabilities?.length || 0) > 0 ? "yes" : "no")
        : "no",
      contingentLiabilitiesDescription: userInput.contingentLiabilities?.[0]?.description || "",
      contingentLiabilitiesAmount: userInput.contingentLiabilities?.[0]?.amount ?? undefined,
      hasLiabilitiesOver5Years: userInput.liabilitiesOver5Years !== undefined
        ? (userInput.liabilitiesOver5Years > 0 ? "yes" : "no")
        : "yes",
      liabilitiesOver5YearsAmount: userInput.liabilitiesOver5Years ?? undefined,
      liabilitiesOver5YearsComment: userInput.liabilitiesOver5YearsComment || "",
      goodwillDepreciationYears: userInput.goodwillDepreciationYears ?? undefined,
      goodwillDepreciationReason: userInput.goodwillDepreciationReason || "",
      hasLoansToBoard: userInput.loansToBoard !== undefined
        ? ((userInput.loansToBoard?.length || 0) > 0 ? "yes" : "no")
        : "yes",
      loansToBoardAmount: userInput.loansToBoard?.find(l => l.category === 'styrelse')?.amount ?? undefined,
      loansToCEOAmount: userInput.loansToBoard?.find(l => l.category === 'vd')?.amount ?? undefined,
      hasRelatedPartyTransactions: userInput.hasRelatedPartyTransactions !== undefined
        ? (userInput.hasRelatedPartyTransactions ? "yes" : "no")
        : "no",
      relatedPartyType: (userInput.relatedPartyTransactions?.[0]?.partyType as any) || "owner",
      relatedPartyDescription: userInput.relatedPartyTransactions?.[0]?.description || "",
      relatedPartyAmount: userInput.relatedPartyTransactions?.[0]?.amount ?? undefined,
      isSubsidiary: userInput.isSubsidiary !== undefined
        ? (userInput.isSubsidiary ? "yes" : "no")
        : "no",
      parentCompanyName: userInput.parentCompany?.name || "",
      parentCompanyOrgNumber: userInput.parentCompany?.organizationNumber || "",
      parentCompanyMunicipality: userInput.parentCompany?.municipality || "",
      // Avskrivningar för anläggningstillgångar
      intangibleDepreciationMethod: userInput.fixedAssetDepreciation?.intangible?.method || "Linjär",
      intangibleUsefulLife: userInput.fixedAssetDepreciation?.intangible?.usefulLife ?? undefined,
      buildingsDepreciationMethod: userInput.fixedAssetDepreciation?.buildings?.method || "Linjär",
      buildingsUsefulLife: userInput.fixedAssetDepreciation?.buildings?.usefulLife ?? undefined,
      machineryDepreciationMethod: userInput.fixedAssetDepreciation?.machinery?.method || "Linjär",
      machineryUsefulLife: userInput.fixedAssetDepreciation?.machinery?.usefulLife ?? undefined,
      // Tjänste-/entreprenaduppdrag
      hasServiceContracts: userInput.hasServiceContracts !== undefined
        ? (userInput.hasServiceContracts ? "yes" : "no")
        : "yes",
      serviceContractMethod: userInput.serviceContractMethod || "huvudregeln",
      serviceContractIndirectCosts: userInput.serviceContractIndirectCosts || false,
      // Egentillverkade varor
      hasSelfManufacturedGoods: userInput.hasSelfManufacturedGoods !== undefined
        ? (userInput.hasSelfManufacturedGoods ? "yes" : "no")
        : "yes",
      selfManufacturedGoodsIndirectCosts: userInput.selfManufacturedGoodsIndirectCosts || false,
      // Exceptionella poster
      hasExceptionalItems: userInput.hasExceptionalItems !== undefined
        ? (userInput.hasExceptionalItems ? "yes" : "no")
        : "no",
      exceptionalItemDescription: userInput.exceptionalItems?.[0]?.nature || "",
      exceptionalItemAmount: userInput.exceptionalItems?.[0]?.amount ?? undefined,
      exceptionalItemIsIncome: userInput.exceptionalItems?.[0]?.isIncome || false,
    },
  });

  const hasPledgedAssets = watch("hasPledgedAssets") === "yes";
  const hasContingentLiabilities = watch("hasContingentLiabilities") === "yes";
  const hasLiabilitiesOver5Years = watch("hasLiabilitiesOver5Years") === "yes";
  const hasLoansToBoard = watch("hasLoansToBoard") === "yes";
  const hasRelatedPartyTransactions = watch("hasRelatedPartyTransactions") === "yes";
  const isSubsidiary = watch("isSubsidiary") === "yes";
  const hasServiceContracts = watch("hasServiceContracts") === "yes";
  const serviceContractMethod = watch("serviceContractMethod");
  const hasSelfManufacturedGoods = watch("hasSelfManufacturedGoods") === "yes";
  const hasExceptionalItems = watch("hasExceptionalItems") === "yes";
  const goodwillDepreciationYears = watch("goodwillDepreciationYears");

  // Load board members
  useEffect(() => {
    if (guestMode) {
      // In guest mode, use board members from parent props
      if (guestBoardMembers) {
        setBoardMembers(guestBoardMembers);
      }
      setBoardMembersLoaded(true);
      return;
    }

    const loadBoardMembers = async () => {
      try {
        const response = await companiesApi.getBoardMembers(company.id);
        if (response.success && response.data) {
          setBoardMembers(response.data.boardMembers);
        }
      } catch (err) {
        console.error("Failed to load board members:", err);
        toast.error("Kunde inte ladda styrelseledamöter");
      } finally {
        setBoardMembersLoaded(true);
      }
    };

    loadBoardMembers();
  }, [company.id, guestMode, guestBoardMembers]);

  // Validate board members - at least one chairman is ALWAYS required (K2, ABL)
  const hasBoardChairperson = boardMembers.some((m) => m.role === "Styrelseordförande");
  const boardMembersValid = boardMembers.length > 0 && hasBoardChairperson;

  const onSubmit = async (data: FormData) => {
    // Check that board members are valid before submitting
    if (!boardMembersValid) {
      setError("Du måste lägga till minst en styrelseordförande innan du kan fortsätta.");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const input: Partial<UserInput> = {
        businessDescription: data.businessDescription,
        significantEvents: data.significantEvents || undefined,
        eventsAfterBalanceDate: data.eventsAfterBalanceDate || undefined,
        averageEmployees: data.averageEmployees ?? undefined,
      };

      // Ställda säkerheter
      if (data.hasPledgedAssets === "yes" && data.pledgedAssetsDescription) {
        input.pledgedAssets = [
          {
            type: data.pledgedAssetsType,
            description: data.pledgedAssetsDescription,
            amount: data.pledgedAssetsAmount,
          },
        ];
      } else {
        input.pledgedAssets = [];
      }

      // Eventualförpliktelser
      if (data.hasContingentLiabilities === "yes" && data.contingentLiabilitiesDescription) {
        input.contingentLiabilities = [
          {
            description: data.contingentLiabilitiesDescription,
            amount: data.contingentLiabilitiesAmount,
          },
        ];
      } else {
        input.contingentLiabilities = [];
      }

      // Skulder >5 år
      if (data.hasLiabilitiesOver5Years === "yes" && data.liabilitiesOver5YearsAmount > 0) {
        input.liabilitiesOver5Years = data.liabilitiesOver5YearsAmount;
        input.liabilitiesOver5YearsComment = data.liabilitiesOver5YearsComment || undefined;
      } else {
        input.liabilitiesOver5Years = undefined;
        input.liabilitiesOver5YearsComment = undefined;
      }

      // Goodwill avskrivningstid
      if (showGoodwillSection && data.goodwillDepreciationYears > 0) {
        input.goodwillDepreciationYears = data.goodwillDepreciationYears;
        if (data.goodwillDepreciationYears > 5 && data.goodwillDepreciationReason) {
          input.goodwillDepreciationReason = data.goodwillDepreciationReason;
        }
      }

      // Lån till ledande befattningshavare (separata fält för styrelse och VD)
      if (data.hasLoansToBoard === "yes") {
        const loans: Array<{ category: 'styrelse' | 'vd' | 'other'; amount: number }> = [];
        if (data.loansToBoardAmount > 0) {
          loans.push({ category: "styrelse", amount: data.loansToBoardAmount });
        }
        if (data.loansToCEOAmount > 0) {
          loans.push({ category: "vd", amount: data.loansToCEOAmount });
        }
        input.loansToBoard = loans;
      } else {
        input.loansToBoard = [];
      }

      // Närstående transaktioner
      input.hasRelatedPartyTransactions = data.hasRelatedPartyTransactions === "yes";
      if (data.hasRelatedPartyTransactions === "yes" && data.relatedPartyDescription) {
        input.relatedPartyTransactions = [
          {
            partyType: data.relatedPartyType,
            party: data.relatedPartyType,
            description: data.relatedPartyDescription,
            amount: data.relatedPartyAmount,
          },
        ];
      } else {
        input.relatedPartyTransactions = [];
      }

      // Moderföretag
      input.isSubsidiary = data.isSubsidiary === "yes";
      if (data.isSubsidiary === "yes" && data.parentCompanyName) {
        input.parentCompany = {
          name: data.parentCompanyName,
          organizationNumber: data.parentCompanyOrgNumber,
          municipality: data.parentCompanyMunicipality,
        };
      } else {
        input.parentCompany = undefined;
      }

      // Anläggningstillgångar - detaljerad not + avskrivningsinfo
      if (showFixedAssetDepreciationQuestion) {
        // Backward-compatible: populate fixedAssetDepreciation from detailed notes
        input.fixedAssetDepreciation = {};
        const fixedAssetNotes: NonNullable<UserInput['fixedAssetNotes']> = {};

        if (showIntangibleAssets && intangibleEnabled) {
          input.fixedAssetDepreciation.intangible = {
            method: intangibleNote.depreciationMethod,
            usefulLife: intangibleNote.usefulLife,
          };
          fixedAssetNotes.intangible = intangibleNote;
        }
        if (showBuildings && buildingsEnabled) {
          input.fixedAssetDepreciation.buildings = {
            method: buildingsNote.depreciationMethod,
            usefulLife: buildingsNote.usefulLife,
          };
          fixedAssetNotes.buildings = buildingsNote;
        }
        if (showMachinery && machineryEnabled) {
          input.fixedAssetDepreciation.machinery = {
            method: machineryNote.depreciationMethod,
            usefulLife: machineryNote.usefulLife,
          };
          fixedAssetNotes.machinery = machineryNote;
        }

        input.fixedAssetNotes = fixedAssetNotes;
      }

      // Tjänste-/entreprenaduppdrag
      if (showServiceContractsQuestion) {
        input.hasServiceContracts = data.hasServiceContracts === "yes";
        if (data.hasServiceContracts === "yes") {
          input.serviceContractMethod = data.serviceContractMethod;
          if (data.serviceContractMethod === 'alternativregeln') {
            input.serviceContractIndirectCosts = data.serviceContractIndirectCosts;
          }
        }
      }

      // Egentillverkade varor
      if (showSelfManufacturedGoodsQuestion) {
        input.hasSelfManufacturedGoods = data.hasSelfManufacturedGoods === "yes";
        if (data.hasSelfManufacturedGoods === "yes") {
          input.selfManufacturedGoodsIndirectCosts = data.selfManufacturedGoodsIndirectCosts;
        }
      }

      // Exceptionella poster (ÅRL 5:19)
      if (showExceptionalItemsQuestion) {
        input.hasExceptionalItems = data.hasExceptionalItems === "yes";
        if (data.hasExceptionalItems === "yes" && data.exceptionalItemDescription) {
          input.exceptionalItems = [
            {
              nature: data.exceptionalItemDescription,
              amount: data.exceptionalItemAmount,
              isIncome: data.exceptionalItemIsIncome,
            },
          ];
        } else {
          input.exceptionalItems = [];
        }
      }

      // Dynamiskt tillagda noter
      if (additionalNotes.length > 0) {
        input.additionalNotes = additionalNotes;
      } else {
        input.additionalNotes = undefined;
      }

      // K3: Nettoomsättningens fördelning
      if (isK3 && revenueSplit) {
        input.revenueSplit = revenueSplit;
      }

      // K3: Komponentavskrivning
      if (isK3 && componentDepreciation) {
        input.componentDepreciation = componentDepreciation;
      }

      // K3: Leasingavtal
      if (isK3 && leasing) {
        input.leasing = leasing;
      }

      // K3: Jämförelsetal för noter
      if (isK3 && comparativeNotes) {
        input.comparativeNotes = comparativeNotes;
      }

      // Include currentStep to track progress
      input.currentStep = 4;

      if (onSaveInput) {
        // Guest mode
        const result = await onSaveInput(input);
        if (result.success) {
          if (result.report) onUpdate(result.report);
          if (onNavigateStep) onNavigateStep(nextStep);
          else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
        } else {
          setError(result.error || "Kunde inte spara");
        }
      } else {
        // Authenticated mode
        const response = await reportsApi.updateInput(report.id, input);
        if (response.success && response.data) {
          onUpdate(response.data.report);
          if (onNavigateStep) onNavigateStep(nextStep);
          else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
        } else {
          setError(response.error?.message || "Kunde inte spara");
        }
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Ett fel uppstod vid sparning");
      setError("Ett fel uppstod vid sparning");
    } finally {
      setIsSaving(false);
    }
  };

  const showEmployeesSection = triggeredNotes.includes("AVERAGE_EMPLOYEES");
  const showPledgedAssetsQuestion = triggeredNotes.includes("PLEDGED_ASSETS_QUESTION");
  const showContingentLiabilitiesQuestion = triggeredNotes.includes("CONTINGENT_LIABILITIES_QUESTION");
  const showLiabilitiesOver5YearsQuestion = triggeredNotes.includes("LIABILITIES_OVER_5_YEARS_QUESTION");
  const showGoodwillSection = triggeredNotes.includes("GOODWILL_DEPRECIATION");
  const showLoansToBoardSection = triggeredNotes.includes("LOANS_TO_BOARD_MEMBERS");
  const showRelatedPartyQuestion = triggeredNotes.includes("RELATED_PARTY_TRANSACTIONS_QUESTION");
  const showParentCompanyQuestion = triggeredNotes.includes("PARENT_COMPANY_QUESTION");
  const showFixedAssetDepreciationQuestion = triggeredNotes.includes("FIXED_ASSETS_DEPRECIATION_QUESTION");
  const showServiceContractsQuestion = triggeredNotes.includes("SERVICE_CONTRACTS_QUESTION");
  const showSelfManufacturedGoodsQuestion = triggeredNotes.includes("SELF_MANUFACTURED_GOODS_QUESTION");
  const showExceptionalItemsQuestion = triggeredNotes.includes("EXCEPTIONAL_ITEMS_QUESTION");

  // Get asset type visibility from triggered notes (backend checks per account range)
  const showIntangibleAssets = triggeredNotes.includes("HAS_INTANGIBLE_ASSETS") ||
    (rawSie?.balanceSheet?.assets?.fixedAssets?.intangible ?? 0) > 0;
  const showBuildings = triggeredNotes.includes("HAS_BUILDINGS");
  const showMachinery = triggeredNotes.includes("HAS_MACHINERY");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Komplettera uppgifter
        </h2>
        <p className="text-gray-600">
          {isManual
            ? "Fyll i de uppgifter som krävs för årsredovisningen baserat på dina angivna värden."
            : "Fyll i de uppgifter som inte kan hämtas från SIE-filen."}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Styrelse & VD - Obligatoriskt */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Users className="text-blue-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Styrelse & VD</h3>
            <p className="text-sm text-gray-500">Krävs för underskrifter</p>
          </div>
        </div>

        {guestMode ? (
          boardMembersLoaded ? (
            <BoardMembersSection
              companyId="guest"
              members={boardMembers}
              onMembersChange={(members) => {
                setBoardMembers(members);
                if (onGuestBoardMembersChange) onGuestBoardMembersChange(members);
              }}
              onSaveLocal={onSaveLocalBoardMembers}
              editable
              showValidation
              compact
            />
          ) : (
            <div className="py-4 text-center text-gray-500">
              Laddar...
            </div>
          )
        ) : boardMembersLoaded ? (
          <BoardMembersSection
            companyId={company.id}
            members={boardMembers}
            onMembersChange={setBoardMembers}
            editable
            showValidation
            compact
          />
        ) : (
          <div className="py-4 text-center text-gray-500">
            Laddar styrelseledamöter...
          </div>
        )}
      </div>

      {/* Förvaltningsberättelse */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <Building2 className="text-primary-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Förvaltningsberättelse</h3>
            <p className="text-sm text-gray-500">Obligatoriskt enligt K2 5.3</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verksamhetsbeskrivning *
            </label>
            <textarea
              {...register("businessDescription", {
                required: "Verksamhetsbeskrivning krävs",
              })}
              rows={3}
              className="input"
              placeholder="Beskriv bolagets verksamhet, t.ex. 'Bolaget bedriver konsultverksamhet inom IT'"
            />
            {errors.businessDescription && (
              <p className="text-sm text-red-600 mt-1">
                {errors.businessDescription.message}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Väsentliga händelser under räkenskapsåret
              </label>
              <ToggleSwitch
                checked={showSignificantEvents}
                onChange={setShowSignificantEvents}
                label="Visa väsentliga händelser"
              />
            </div>
            {showSignificantEvents && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">
                  Beskriv t.ex. nyetablering av verksamhet, omstruktureringar, större kontrakt eller avtal,
                  förvärv eller avyttring av dotterbolag, väsentliga förändringar i omsättning eller resultat,
                  nya marknader eller produkter.
                </p>
                <textarea
                  {...register("significantEvents")}
                  rows={2}
                  className="input"
                  placeholder="T.ex. 'Bolaget har under året tecknat ett treårigt samarbetsavtal med...'"
                />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Väsentliga händelser efter balansdagen
              </label>
              <ToggleSwitch
                checked={showEventsAfterBalanceDate}
                onChange={setShowEventsAfterBalanceDate}
                label="Händelser efter balansdagen"
              />
            </div>
            {showEventsAfterBalanceDate && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">
                  Beskriv väsentliga händelser som inträffat efter räkenskapsårets slut,
                  t.ex. större förvärv, nedläggning av verksamhetsgrenar, rättstvister eller väsentliga förändringar i marknadsförhållanden.
                </p>
                <textarea
                  {...register("eventsAfterBalanceDate")}
                  rows={2}
                  className="input"
                  placeholder="T.ex. 'Efter räkenskapsårets slut har bolaget förvärvat...'"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Personal */}
      {showEmployeesSection && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="text-blue-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Medelantal anställda</h3>
                <p className="text-sm text-gray-500">ÅRL 5:20, K2 18.9</p>
              </div>
            </div>
            <TriggeredNoteBadge isManual={isManual} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Medelantal anställda under året *
            </label>
            <input
              type="number"
              min="0"
              step="0.1"
              {...register("averageEmployees", {
                valueAsNumber: true,
                validate: (v) => {
                  if (v === undefined || v === null || isNaN(v)) return "Medelantal anställda krävs";
                  if (v < 0) return "Kan inte vara negativt";
                  return true;
                },
              })}
              className={`input w-32 ${errors.averageEmployees ? "border-red-300" : ""}`}
              placeholder="0"
            />
            {errors.averageEmployees && (
              <p className="text-sm text-red-600 mt-1">
                {errors.averageEmployees.message}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Räkna i heltidstjänster. Inkluderar ägare som arbetar i företaget.
            </p>
            {prevInput?.averageEmployees != null && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 mt-2">
                <span className="font-medium">Föregående år:</span>{" "}
                {prevInput.averageEmployees} anställda
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ställda säkerheter */}
      {showPledgedAssetsQuestion && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield className="text-purple-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Ställda säkerheter</h3>
                <p className="text-sm text-gray-500">ÅRL 5:14</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TriggeredNoteBadge isManual={isManual} />
              <ToggleSwitch
                checked={hasPledgedAssets}
                onChange={(checked) => setValue("hasPledgedAssets", checked ? "yes" : "no")}
                label="Ställda säkerheter"
              />
            </div>
          </div>

          {hasPledgedAssets && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              {(() => {
                const longTerm = rawSie?.balanceSheet?.equityAndLiabilities?.longTermLiabilities ?? 0;
                const shortTerm = rawSie?.balanceSheet?.equityAndLiabilities?.shortTermLiabilities ?? 0;
                const totalLoans = longTerm + shortTerm;
                return totalLoans > 0 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <span className="font-medium">Enligt bokföringen:</span>{" "}
                    Totala skulder uppgår till {formatNumber(totalLoans)} kr
                    {longTerm > 0 && shortTerm > 0 && (
                      <span className="text-blue-600"> (långfristiga {formatNumber(longTerm)} kr, kortfristiga {formatNumber(shortTerm)} kr)</span>
                    )}
                  </div>
                ) : null;
              })()}
              {prevInput?.pledgedAssets && prevInput.pledgedAssets.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  <span className="font-medium">Föregående år:</span>{" "}
                  {prevInput.pledgedAssets.map((a, i) => (
                    <span key={i}>
                      {i > 0 && ", "}
                      {a.description}: {formatNumber(a.amount)} kr
                    </span>
                  ))}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Typ av säkerhet
                </label>
                <select
                  {...register("pledgedAssetsType")}
                  className="input"
                >
                  <option value="corporate_mortgage">Företagsinteckning</option>
                  <option value="property_mortgage">Fastighetsinteckning</option>
                  <option value="chattel_mortgage">Lös egendom (pantbrev)</option>
                  <option value="other">Övrig säkerhet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beskrivning av säkerhet *
                </label>
                <input
                  type="text"
                  {...register("pledgedAssetsDescription", {
                    required: hasPledgedAssets ? "Beskrivning krävs" : false,
                  })}
                  className={`input ${errors.pledgedAssetsDescription ? "border-red-300" : ""}`}
                  placeholder="T.ex. Företagsinteckningar för checkkredit"
                />
                {errors.pledgedAssetsDescription && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.pledgedAssetsDescription.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belopp (kr) *
                </label>
                <input
                  type="number"
                  min="0"
                  {...register("pledgedAssetsAmount", {
                    valueAsNumber: true,
                    validate: hasPledgedAssets ? (v) => {
                      if (v === undefined || v === null || isNaN(v)) return "Belopp krävs";
                      if (v < 1) return "Beloppet måste vara större än 0";
                      return true;
                    } : undefined,
                  })}
                  className={`input w-48 ${errors.pledgedAssetsAmount ? "border-red-300" : ""}`}
                  placeholder="0"
                />
                {errors.pledgedAssetsAmount && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.pledgedAssetsAmount.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Eventualförpliktelser */}
      {showContingentLiabilitiesQuestion && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="text-orange-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Eventualförpliktelser</h3>
                <p className="text-sm text-gray-500">ÅRL 5:15 - Borgensåtaganden, garantier m.m.</p>
              </div>
            </div>
            <ToggleSwitch
              checked={hasContingentLiabilities}
              onChange={(checked) => setValue("hasContingentLiabilities", checked ? "yes" : "no")}
              label="Eventualförpliktelser"
            />
          </div>

          {hasContingentLiabilities && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              {prevInput?.contingentLiabilities && prevInput.contingentLiabilities.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  <span className="font-medium">Föregående år:</span>{" "}
                  {prevInput.contingentLiabilities.map((c, i) => (
                    <span key={i}>
                      {i > 0 && ", "}
                      {c.description}: {formatNumber(c.amount)} kr
                    </span>
                  ))}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beskrivning *
                </label>
                <input
                  type="text"
                  {...register("contingentLiabilitiesDescription", {
                    required: hasContingentLiabilities ? "Beskrivning krävs" : false,
                  })}
                  className={`input ${errors.contingentLiabilitiesDescription ? "border-red-300" : ""}`}
                  placeholder="T.ex. Borgensåtagande för dotterbolag"
                />
                {errors.contingentLiabilitiesDescription && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.contingentLiabilitiesDescription.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belopp (kr) *
                </label>
                <input
                  type="number"
                  min="0"
                  {...register("contingentLiabilitiesAmount", {
                    valueAsNumber: true,
                    validate: hasContingentLiabilities ? (v) => {
                      if (v === undefined || v === null || isNaN(v)) return "Belopp krävs";
                      if (v < 1) return "Beloppet måste vara större än 0";
                      return true;
                    } : undefined,
                  })}
                  className={`input w-48 ${errors.contingentLiabilitiesAmount ? "border-red-300" : ""}`}
                  placeholder="0"
                />
                {errors.contingentLiabilitiesAmount && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.contingentLiabilitiesAmount.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Skulder förfaller efter mer än 5 år */}
      {showLiabilitiesOver5YearsQuestion && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Clock className="text-indigo-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Skulder förfaller efter mer än 5 år</h3>
                <p className="text-sm text-gray-500">ÅRL 5:13</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TriggeredNoteBadge isManual={isManual} />
              <ToggleSwitch
                checked={hasLiabilitiesOver5Years}
                onChange={(checked) => setValue("hasLiabilitiesOver5Years", checked ? "yes" : "no")}
                label="Skulder över 5 år"
              />
            </div>
          </div>

          {hasLiabilitiesOver5Years && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              {(() => {
                const longTerm = rawSie?.balanceSheet?.equityAndLiabilities?.longTermLiabilities ?? 0;
                return longTerm > 0 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <span className="font-medium">Enligt bokföringen:</span>{" "}
                    Totala långfristiga skulder uppgår till {formatNumber(longTerm)} kr.
                    Ange hur stor del som förfaller efter mer än 5 år.
                  </div>
                ) : null;
              })()}
              {prevInput?.liabilitiesOver5Years != null && prevInput.liabilitiesOver5Years > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  <span className="font-medium">Föregående år:</span>{" "}
                  {formatNumber(prevInput.liabilitiesOver5Years)} kr
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belopp som förfaller efter mer än 5 år (kr) *
                </label>
                <input
                  type="number"
                  min="0"
                  {...register("liabilitiesOver5YearsAmount", {
                    valueAsNumber: true,
                    validate: hasLiabilitiesOver5Years ? (v) => {
                      if (v === undefined || v === null || isNaN(v)) return "Belopp krävs";
                      if (v < 1) return "Beloppet måste vara större än 0";
                      return true;
                    } : undefined,
                  })}
                  className={`input w-48 ${errors.liabilitiesOver5YearsAmount ? "border-red-300" : ""}`}
                  placeholder="0"
                />
                {errors.liabilitiesOver5YearsAmount && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.liabilitiesOver5YearsAmount.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kommentar (valfritt)
                </label>
                <textarea
                  {...register("liabilitiesOver5YearsComment")}
                  className="input"
                  rows={2}
                  placeholder="T.ex. vilken typ av skuld, kreditgivare, räntevillkor"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Goodwill avskrivningstid */}
      {showGoodwillSection && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <FileText className="text-teal-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Goodwill avskrivningstid</h3>
                <p className="text-sm text-gray-500">ÅRL 5:9 - Skälen för tillämpad avskrivningstid</p>
              </div>
            </div>
            <TriggeredNoteBadge isManual={isManual} />
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Avskrivningstid för goodwill (år) *
              </label>
              <input
                type="number"
                min="1"
                max="10"
                {...register("goodwillDepreciationYears", {
                  valueAsNumber: true,
                  validate: (v) => {
                    if (v === undefined || v === null || isNaN(v)) return "Avskrivningstid krävs";
                    if (v < 1) return "Minst 1 år";
                    if (v > 10) return "Max 10 år";
                    return true;
                  },
                })}
                className={`input w-32 ${errors.goodwillDepreciationYears ? "border-red-300" : ""}`}
                placeholder="5"
              />
              {errors.goodwillDepreciationYears && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.goodwillDepreciationYears.message}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Enligt K2 får goodwill skrivas av på högst 5 år om nyttjandeperioden inte kan fastställas.
              </p>
              {prevInput?.goodwillDepreciationYears != null && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 mt-2">
                  <span className="font-medium">Föregående år:</span>{" "}
                  {prevInput.goodwillDepreciationYears} år
                </div>
              )}
            </div>

            {goodwillDepreciationYears > 5 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivering till avskrivningstid längre än 5 år *
                </label>
                <textarea
                  {...register("goodwillDepreciationReason", {
                    required: goodwillDepreciationYears > 5
                      ? "Motivering krävs när avskrivningstid överstiger 5 år"
                      : false,
                  })}
                  rows={2}
                  className="input"
                  placeholder="Ange skälen för den längre avskrivningstiden, t.ex. kundrelationer, varumärke"
                />
                {errors.goodwillDepreciationReason && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.goodwillDepreciationReason.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  ÅRL 5:9 kräver att skälen för avskrivningstid längre än 5 år anges i not.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lån till ledande befattningshavare */}
      {showLoansToBoardSection && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Landmark className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Lån till ledande befattningshavare</h3>
                <p className="text-sm text-gray-500">ÅRL 5:12</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TriggeredNoteBadge isManual={isManual} />
              <ToggleSwitch
                checked={hasLoansToBoard}
                onChange={(checked) => setValue("hasLoansToBoard", checked ? "yes" : "no")}
                label="Lån till befattningshavare"
              />
            </div>
          </div>

          {hasLoansToBoard && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>OBS!</strong> {isManual
                    ? "Baserat på angivna uppgifter kan det finnas fordringar på ledande befattningshavare."
                    : "SIE-filen visar fordringar på konto 1680-1689."}{" "}
                  Uppge beloppet för lån till styrelseledamöter, VD eller andra ledande befattningshavare.
                </p>
              </div>
              {prevInput?.loansToBoard && prevInput.loansToBoard.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  <span className="font-medium">Föregående år:</span>{" "}
                  {prevInput.loansToBoard.map((l, i) => (
                    <span key={i}>
                      {i > 0 && ", "}
                      {l.category === "styrelse" ? "Styrelse" : l.category === "vd" ? "VD" : "Övrigt"}: {formatNumber(l.amount)} kr
                    </span>
                  ))}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lån till styrelseledamöter (kr)
                </label>
                <input
                  type="number"
                  min="0"
                  {...register("loansToBoardAmount", { valueAsNumber: true })}
                  className="input w-48"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lån till VD (kr)
                </label>
                <input
                  type="number"
                  min="0"
                  {...register("loansToCEOAmount", { valueAsNumber: true })}
                  className="input w-48"
                  placeholder="0"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transaktioner med närstående */}
      {showRelatedPartyQuestion && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                <Link2 className="text-pink-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Transaktioner med närstående</h3>
                <p className="text-sm text-gray-500">ÅRL 5:23, K2 18.10</p>
              </div>
            </div>
            <ToggleSwitch
              checked={hasRelatedPartyTransactions}
              onChange={(checked) => setValue("hasRelatedPartyTransactions", checked ? "yes" : "no")}
              label="Närstående transaktioner"
            />
          </div>

          {hasRelatedPartyTransactions && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Enligt K2-förenkling behöver endast <strong>väsentliga transaktioner</strong> som
                  skett <strong>utanför normala villkor</strong> med närstående redovisas.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Typ av närstående
                </label>
                <select
                  {...register("relatedPartyType")}
                  className="input"
                >
                  <option value="owner">Ägare</option>
                  <option value="board">Styrelseledamot</option>
                  <option value="ceo">VD</option>
                  <option value="family">Familjemedlem till ovan</option>
                  <option value="related_company">Närstående företag</option>
                  <option value="other">Annan närstående</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beskrivning av transaktionen *
                </label>
                <input
                  type="text"
                  {...register("relatedPartyDescription", {
                    required: hasRelatedPartyTransactions ? "Beskrivning krävs" : false,
                  })}
                  className={`input ${errors.relatedPartyDescription ? "border-red-300" : ""}`}
                  placeholder="T.ex. Hyra av lokal från ägare under marknadspris"
                />
                {errors.relatedPartyDescription && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.relatedPartyDescription.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belopp (kr) *
                </label>
                <input
                  type="number"
                  min="0"
                  {...register("relatedPartyAmount", {
                    valueAsNumber: true,
                    validate: hasRelatedPartyTransactions ? (v) => {
                      if (v === undefined || v === null || isNaN(v)) return "Belopp krävs";
                      if (v < 1) return "Beloppet måste vara större än 0";
                      return true;
                    } : undefined,
                  })}
                  className={`input w-48 ${errors.relatedPartyAmount ? "border-red-300" : ""}`}
                  placeholder="0"
                />
                {errors.relatedPartyAmount && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.relatedPartyAmount.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Moderföretag (om dotterbolag) */}
      {showParentCompanyQuestion && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <Building2 className="text-gray-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Koncerntillhörighet</h3>
                <p className="text-sm text-gray-500">K2 19.2 - Är företaget ett dotterbolag?</p>
              </div>
            </div>
            <ToggleSwitch
              checked={isSubsidiary}
              onChange={(checked) => setValue("isSubsidiary", checked ? "yes" : "no")}
              label="Koncerntillhörighet"
            />
          </div>

          {isSubsidiary && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moderföretagets namn *
                </label>
                <input
                  type="text"
                  {...register("parentCompanyName", {
                    required: isSubsidiary ? "Moderföretagets namn krävs" : false,
                  })}
                  className="input"
                  placeholder="T.ex. Holdingbolaget AB"
                />
                {errors.parentCompanyName && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.parentCompanyName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moderföretagets organisationsnummer *
                </label>
                <input
                  type="text"
                  {...register("parentCompanyOrgNumber", {
                    required: isSubsidiary ? "Organisationsnummer krävs" : false,
                  })}
                  className="input w-48"
                  placeholder="556xxx-xxxx"
                />
                {errors.parentCompanyOrgNumber && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.parentCompanyOrgNumber.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moderföretagets säte (kommun) *
                </label>
                <input
                  type="text"
                  {...register("parentCompanyMunicipality", {
                    required: isSubsidiary ? "Moderföretagets säte krävs" : false,
                  })}
                  className="input"
                  placeholder="T.ex. Stockholm"
                />
                {errors.parentCompanyMunicipality && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.parentCompanyMunicipality.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Anläggningstillgångar - detaljerade noter per typ */}
      {showFixedAssetDepreciationQuestion && (
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Wrench className="text-slate-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Anläggningstillgångar</h3>
              <p className="text-sm text-gray-500">ÅRL 5:8, K2 kap 10</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              Ange avskrivningsmetod, nyttjandeperiod och detaljerad specifikation för varje tillgångstyp.
              Utgående redovisat värde ska matcha balansräkningen. Enligt K2 antas nyttjandeperioden vara 5 år om den inte kan fastställas.
            </p>
          </div>

          <div className="space-y-4">
            {showIntangibleAssets && (
              <FixedAssetNoteCard
                type="intangible"
                title="Immateriella anläggningstillgångar"
                legalRef="Konto 10xx"
                icon={<BookOpen className="text-slate-600" size={16} />}
                sieBalance={effectiveSie?.balanceSheet?.assets?.fixedAssets?.intangible ?? 0}
                data={intangibleNote}
                onChange={setIntangibleNote}
                enabled={intangibleEnabled}
                onToggle={setIntangibleEnabled}
                usefulLifeHint="K2: Antas vara 5 år om inte annat kan fastställas"
                usefulLifeMax={20}
                isManual={isManual}
              />
            )}

            {showBuildings && (
              <FixedAssetNoteCard
                type="buildings"
                title="Byggnader och mark"
                legalRef="Konto 11xx"
                icon={<Building2 className="text-slate-600" size={16} />}
                sieBalance={effectiveSie?.balanceSheet?.assets?.fixedAssets?.buildings ?? 0}
                data={buildingsNote}
                onChange={setBuildingsNote}
                enabled={buildingsEnabled}
                onToggle={setBuildingsEnabled}
                usefulLifeHint="Vanligtvis 25-50 år för byggnader"
                usefulLifeMax={100}
                isManual={isManual}
              />
            )}

            {showMachinery && (
              <FixedAssetNoteCard
                type="machinery"
                title="Maskiner och inventarier"
                legalRef="Konto 12xx"
                icon={<Wrench className="text-slate-600" size={16} />}
                sieBalance={effectiveSie?.balanceSheet?.assets?.fixedAssets?.machinery ?? 0}
                data={machineryNote}
                onChange={setMachineryNote}
                enabled={machineryEnabled}
                onToggle={setMachineryEnabled}
                usefulLifeHint="Vanligtvis 3-10 år"
                usefulLifeMax={30}
                isManual={isManual}
              />
            )}
          </div>
        </div>
      )}

      {/* Tjänste-/entreprenaduppdrag */}
      {showServiceContractsQuestion && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <ClipboardList className="text-cyan-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Tjänste- och entreprenaduppdrag</h3>
                <p className="text-sm text-gray-500">K2 kapitel 6</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TriggeredNoteBadge isManual={isManual} />
              <ToggleSwitch
                checked={hasServiceContracts}
                onChange={(checked) => setValue("hasServiceContracts", checked ? "yes" : "no")}
                label="Tjänsteuppdrag"
              />
            </div>
          </div>

          {hasServiceContracts && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>OBS!</strong> {isManual
                    ? "Baserat på angivna uppgifter kan det finnas pågående tjänste- eller entreprenaduppdrag."
                    : "SIE-filen visar pågående arbete för annans räkning (konto 1470-1479)."}{" "}
                  Ange hur intäktsredovisning sker för tjänste- och entreprenaduppdrag till fast pris.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Intäktsredovisningsmetod
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="huvudregeln"
                      {...register("serviceContractMethod")}
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Huvudregeln</strong> - successiv vinstavräkning (intäkt redovisas i takt med färdigställande)
                    </span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="alternativregeln"
                      {...register("serviceContractMethod")}
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Alternativregeln</strong> - intäkt redovisas när uppdraget väsentligen är fullgjort
                    </span>
                  </label>
                </div>
              </div>

              {serviceContractMethod === 'alternativregeln' && (
                <div className="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
                  <input
                    type="checkbox"
                    id="serviceContractIndirectCosts"
                    {...register("serviceContractIndirectCosts")}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="serviceContractIndirectCosts" className="text-sm text-gray-700">
                    Indirekta utgifter inkluderas i pågående arbeten (K2 6.16)
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Egentillverkade varor */}
      {showSelfManufacturedGoodsQuestion && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Factory className="text-emerald-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Egentillverkade varor</h3>
                <p className="text-sm text-gray-500">K2 12.8</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TriggeredNoteBadge isManual={isManual} />
              <ToggleSwitch
                checked={hasSelfManufacturedGoods}
                onChange={(checked) => setValue("hasSelfManufacturedGoods", checked ? "yes" : "no")}
                label="Egentillverkade varor"
              />
            </div>
          </div>

          {hasSelfManufacturedGoods && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>OBS!</strong> {isManual
                    ? "Baserat på angivna uppgifter kan det finnas egentillverkade varor i lager."
                    : "SIE-filen visar egentillverkade varor i lager (konto 1440-1449)."}{" "}
                  Ange om indirekta tillverkningskostnader inkluderas i anskaffningsvärdet.
                </p>
              </div>
              <div className="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="selfManufacturedGoodsIndirectCosts"
                  {...register("selfManufacturedGoodsIndirectCosts")}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="selfManufacturedGoodsIndirectCosts" className="text-sm text-gray-700">
                  Indirekta tillverkningskostnader inkluderas i anskaffningsvärdet (K2 12.8)
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Enligt K2 12.8 får företag välja att räkna in skälig andel av indirekta tillverkningskostnader
                i anskaffningsvärdet för egentillverkade varor.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Exceptionella poster */}
      {showExceptionalItemsQuestion && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="text-rose-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Exceptionella intäkter och kostnader</h3>
                <p className="text-sm text-gray-500">ÅRL 5:19</p>
              </div>
            </div>
            <ToggleSwitch
              checked={hasExceptionalItems}
              onChange={(checked) => setValue("hasExceptionalItems", checked ? "yes" : "no")}
              label="Exceptionella poster"
            />
          </div>

          {hasExceptionalItems && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Exceptionella poster är intäkter eller kostnader som till sin <strong>storlek eller förekomst</strong> avviker
                  markant från vad som normalt förekommer, t.ex. stora nedskrivningar, omstruktureringskostnader eller rättstvister.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beskrivning av posten *
                </label>
                <input
                  type="text"
                  {...register("exceptionalItemDescription", {
                    required: hasExceptionalItems ? "Beskrivning krävs" : false,
                  })}
                  className={`input ${errors.exceptionalItemDescription ? "border-red-300" : ""}`}
                  placeholder="T.ex. Nedskrivning av goodwill, omstruktureringskostnader"
                />
                {errors.exceptionalItemDescription && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.exceptionalItemDescription.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belopp (kr) *
                </label>
                <input
                  type="number"
                  min="0"
                  {...register("exceptionalItemAmount", {
                    valueAsNumber: true,
                    validate: hasExceptionalItems ? (v) => {
                      if (v === undefined || v === null || isNaN(v)) return "Belopp krävs";
                      if (v < 1) return "Beloppet måste vara större än 0";
                      return true;
                    } : undefined,
                  })}
                  className={`input w-48 ${errors.exceptionalItemAmount ? "border-red-300" : ""}`}
                  placeholder="0"
                />
                {errors.exceptionalItemAmount && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.exceptionalItemAmount.message}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="exceptionalItemIsIncome"
                  {...register("exceptionalItemIsIncome")}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="exceptionalItemIsIncome" className="text-sm text-gray-700">
                  Detta är en intäkt (ej kostnad)
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          K3-SPECIFIKA SEKTIONER
          Visas endast för K3-årsredovisningar
          ═══════════════════════════════════════════════════════════════════ */}

      {/* K3: Uppskjuten skatt (K3 kap 29) */}
      {isK3 && (
        <K3DeferredTaxSection
          report={report}
          onUpdate={onUpdate}
        />
      )}

      {/* K3: Nettoomsättningens fördelning (K3 kap 5) */}
      {isK3 && totalRevenue > 0 && (
        <K3RevenueSplitSection
          userInput={userInput}
          totalRevenue={totalRevenue}
          onChange={setRevenueSplit}
        />
      )}

      {/* K3: Komponentavskrivning (K3 kap 17) */}
      {isK3 && buildingsValue > 0 && (
        <K3ComponentDepreciationSection
          userInput={userInput}
          buildingsValue={buildingsValue}
          onChange={setComponentDepreciation}
        />
      )}

      {/* K3: Leasingavtal (K3 kap 20) */}
      {isK3 && (
        <K3LeaseSection
          userInput={userInput}
          onChange={setLeasing}
        />
      )}

      {/* K3: Jämförelsetal för noter (K3 kap 3.10) */}
      {isK3 && (
        <K3ComparativeNotesSection
          report={report}
          userInput={userInput}
          previousYearUserInput={prevInput}
          onChange={setComparativeNotes}
        />
      )}

      {/* K3: Koncernredovisning (K3 kap 9) */}
      {isK3 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Building2 className="text-indigo-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Koncernredovisning</h3>
                <p className="text-sm text-gray-500">K3 kap 9 - För moderbolag med dotterbolag</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                K3
              </span>
              {!hasSubsidiaries && (
                <ToggleSwitch
                  checked={showConsolidationWizard}
                  onChange={(checked) => setShowConsolidationWizard(checked)}
                  label="Visa koncernredovisning"
                />
              )}
            </div>
          </div>

          {!hasSubsidiaries && !showConsolidationWizard && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Om ditt företag är moderbolag i en koncern och ska upprätta koncernredovisning,
                aktivera denna sektion för att ladda upp dotterbolagens SIE-filer och beräkna
                koncerngoodwill, minoritetsintresse och elimineringar.
              </p>
            </div>
          )}

          {(hasSubsidiaries || showConsolidationWizard) && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <ConsolidationWizard
                report={report}
                onUpdate={onUpdate}
                onClose={() => setShowConsolidationWizard(false)}
              />
            </div>
          )}
        </div>
      )}

      {/* Dynamiskt tillagda noter */}
      {additionalNotes.length > 0 && (
        <div className="space-y-4">
          {additionalNotes.map((note, index) => (
            <div key={note.noteId} className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 text-sm">{note.title}</h3>
                <button
                  type="button"
                  onClick={() => setAdditionalNotes(additionalNotes.filter((_, i) => i !== index))}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Ta bort
                </button>
              </div>
              <textarea
                value={note.content}
                onChange={(e) => {
                  const updated = [...additionalNotes];
                  updated[index] = { ...note, content: e.target.value };
                  setAdditionalNotes(updated);
                }}
                className="input w-full"
                rows={3}
                placeholder="Ange notinnehåll..."
              />
            </div>
          ))}
        </div>
      )}

      {/* Lägg till fler noter */}
      {availableNotesFiltered.length > 0 && (
        <div className="card bg-gray-50">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-200">
              <Plus className="text-gray-500" size={16} />
            </div>
            <h3 className="font-medium text-gray-700 text-sm">Behöver du fler noter?</h3>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedAdditionalNote}
              onChange={(e) => setSelectedAdditionalNote(e.target.value)}
              className="input flex-1 text-sm"
            >
              <option value="">Välj not att lägga till...</option>
              {availableNotesFiltered.map((n) => (
                <option key={n.id} value={n.id}>{n.title}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={!selectedAdditionalNote}
              onClick={() => {
                const noteTemplate = AVAILABLE_ADDITIONAL_NOTES.find((n) => n.id === selectedAdditionalNote);
                if (noteTemplate) {
                  setAdditionalNotes([
                    ...additionalNotes,
                    { noteId: noteTemplate.id, title: noteTemplate.title, content: "" },
                  ]);
                  setSelectedAdditionalNote("");
                }
              }}
              className="btn btn-secondary text-sm px-4 py-2 disabled:opacity-50"
            >
              Lägg till
            </button>
          </div>
        </div>
      )}

      {/* Validation summary */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <div className="flex items-center space-x-2">
            <XCircle size={16} className="text-red-600 flex-shrink-0" />
            <p className="text-sm font-medium text-red-800">
              {Object.keys(errors).length === 1
                ? "1 obligatoriskt fält är inte ifyllt. Kontrollera markerade fält ovan."
                : `${Object.keys(errors).length} obligatoriska fält är inte ifyllda. Kontrollera markerade fält ovan.`}
            </p>
          </div>
        </div>
      )}

      {!boardMembersValid && boardMembersLoaded && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <div className="flex items-start space-x-2">
            <XCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">
              {guestMode
                ? "Minst en styrelseordförande krävs. Du kan lägga till detta nu eller komplettera senare."
                : "Du måste lägga till minst en styrelseordförande innan du kan fortsätta."}
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={() => {
            window.scrollTo(0, 0);
            onNavigateStep ? onNavigateStep(prevStep) : navigate(`/dashboard/wizard/${report.id}/step/${prevStep}`);
          }}
          className="btn btn-secondary flex items-center"
        >
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka
        </button>

        <button
          type="submit"
          disabled={isSaving}
          className="btn btn-primary flex items-center"
        >
          {isSaving ? (
            "Sparar..."
          ) : (
            <>
              Fortsätt
              <ArrowRight size={20} className="ml-2" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
